import { useNavigate } from "react-router-dom";
import { ArrowLeft, ShieldAlert } from "lucide-react";

export default function Disclaimer() {
  const navigate = useNavigate();
  const updated = "February 2026";

  return (
    <div
      className="min-h-screen fc-app-bg text-[#27272A] fc-fade-in"
      data-testid="disclaimer-page"
    >
      <header className="sticky top-0 z-20 fc-header-bar">
        <div className="max-w-4xl mx-auto flex items-center justify-between py-4 px-6 gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={() => navigate(-1)}
              className="w-8 h-8 rounded-lg border border-black/[0.08] grid place-items-center text-[#78716C] hover:text-[#1C1C1C] hover:border-black/[0.2] transition-colors shrink-0"
              data-testid="disclaimer-back-btn"
              aria-label="Back"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <button
              type="button"
              data-testid="disclaimer-logo-btn"
              onClick={() => navigate("/")}
              className="flex items-baseline gap-2.5 sm:gap-3 shrink-0 min-w-0"
              aria-label="MyPlanCost home"
            >
              <span className="fc-heading text-2xl sm:text-[28px] font-extrabold tracking-tight text-[#1C1C1C] leading-none">
                MyPlanCost
              </span>
              <span className="hidden sm:inline text-[10px] uppercase tracking-[0.25em] text-[#A8A29E] font-medium leading-none whitespace-nowrap">
                FlipCost &amp; SpaceCost
              </span>
            </button>
          </div>
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-sage-700 border border-sage-200 rounded-full px-3 py-1 bg-sage-50 shrink-0">
            <ShieldAlert className="w-3 h-3" /> Legal
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10 sm:py-14">
        <h1
          className="fc-heading text-3xl sm:text-4xl tracking-tight text-[#1C1C1C]"
          data-testid="disclaimer-heading"
        >
          Disclaimer & Terms of Use
        </h1>
        <p className="text-xs text-[#A8A29E] mt-2 uppercase tracking-[0.15em]">
          Last updated: {updated}
        </p>

        <div
          className="fc-card mt-8 p-6 sm:p-8 border-l-4 bg-sage-50"
          style={{ borderLeftColor: "var(--fc-sage-700)" }}
          data-testid="disclaimer-summary"
        >
          <div className="fc-label">In plain English</div>
          <p className="text-[15px] text-[#1C1C1C] leading-relaxed mt-2">
            MyPlanCost (FlipCost &amp; SpaceCost) is a planning and decision-support tool. Every cost, profit,
            ROI, margin, value-add, and comparable number it shows is an
            <strong> estimate</strong> based on your inputs, generic regional defaults,
            and simplified formulas. It is <strong>not</strong> a professional
            quote, valuation, appraisal, financial advice, or a guarantee of any
            outcome. Always get quotes from licensed contractors and independent
            advice from qualified professionals before making a financial
            commitment.
          </p>
        </div>

        <Section title="1. No professional advice">
          <p>
            The Service provides renovation cost estimates, comparable-project insights,
            profit and ROI calculations, actual-spend tracking, and related
            analytics for informational and planning purposes only. Nothing the
            Service displays constitutes professional construction, engineering,
            surveying, real-estate, taxation, legal, accounting, investment, or
            financial advice. You should consult suitably qualified and licensed
            professionals in your jurisdiction before acting on anything you see
            in the Service.
          </p>
        </Section>

        <Section title="2. Estimates are approximations">
          <p>
            Default cost rates, regional multipliers, finish-level multipliers,
            scope factors, value-add percentages, comparable-deal statistics, and
            soft-cost assumptions are <strong>generic approximations</strong>.
            They do not reflect the pricing, availability, taxes, code
            requirements, labour conditions, or market dynamics of your specific
            property, region, or project. Actual costs will differ — often
            materially — from anything the Service projects.
          </p>
        </Section>

        <Section title="3. Your inputs, your responsibility">
          <p>
            The Service's output is only as accurate as the data you enter. You
            are solely responsible for the correctness of room sizes, finish
            levels, quantities, rates, purchase prices, sale assumptions, and any
            overrides you make in the Cost Library. We do not independently
            verify anything you enter.
          </p>
        </Section>

        <Section title="4. No warranty">
          <p>
            The Service is provided "AS IS" and "AS AVAILABLE" without warranties
            of any kind, whether express, implied, statutory, or otherwise,
            including but not limited to warranties of merchantability, fitness
            for a particular purpose, accuracy, reliability, non-infringement,
            completeness, or that the Service will be uninterrupted or
            error-free.
          </p>
        </Section>

        <Section title="5. Limitation of liability">
          <p>
            To the maximum extent permitted by law, MyPlanCost (FlipCost &amp; SpaceCost), its operators,
            owners, employees, contributors, and affiliates shall not be liable
            for any direct, indirect, incidental, special, consequential,
            exemplary, or punitive damages — including but not limited to loss
            of profit, loss of revenue, cost overruns, project cancellation,
            property-value shortfalls, business interruption, loss of data, or
            any other intangible losses — arising out of or in connection with
            your use of, or inability to use, the Service, whether based in
            contract, tort (including negligence), strict liability, or any
            other legal theory, even if we have been advised of the possibility
            of such damages.
          </p>
          <p className="mt-3">
            Where the law does not permit the exclusion of certain liabilities,
            our total aggregate liability to you for all claims arising out of
            or relating to the Service is limited to the greater of (a) the
            fees you have paid us in the twelve (12) months immediately prior
            to the event giving rise to the claim, or (b) USD 50.
          </p>
        </Section>

        <Section title="6. Third-party data and comparables">
          <p>
            Any comparable-deal or regional preset data displayed is derived from
            generalised assumptions and/or data voluntarily entered by other
            users of the Service. We make no representation that such data is
            current, accurate, complete, legally admissible, or applicable to
            your property.
          </p>
        </Section>

        <Section title="7. No guarantee of outcome">
          <p>
            Profit, ROI, margin, resale value, rental return, and value-added
            figures shown in the Service are hypothetical projections. Real
            property, construction, and market outcomes are affected by
            variables outside the Service's model, including market conditions,
            timing, financing, regulation, weather, contractor performance, and
            buyer behaviour. <strong>No guarantee, representation, or warranty
            is given that any projected profit, return, value, cost, timeline,
            or sale will be achieved.</strong>
          </p>
        </Section>

        <Section title="8. Indemnity">
          <p>
            You agree to indemnify and hold harmless MyPlanCost (FlipCost &amp; SpaceCost) and its
            operators from and against any claims, damages, losses, liabilities,
            costs, and expenses (including reasonable legal fees) arising out of
            or in connection with your use of the Service, your reliance on any
            output of the Service, or your breach of these terms.
          </p>
        </Section>

        <Section title="9. Acceptance">
          <p>
            By creating an account, signing in, or otherwise using the Service,
            you acknowledge that you have read, understood, and agreed to this
            Disclaimer and Terms of Use in full. If you do not agree, you must
            stop using the Service immediately.
          </p>
        </Section>

        <Section title="10. Updates">
          <p>
            We may update this Disclaimer from time to time. Material changes
            will be reflected by updating the "Last updated" date above.
            Continued use of the Service after changes constitutes acceptance
            of the revised terms.
          </p>
        </Section>

        <p className="text-xs text-[#A8A29E] mt-10">
          If any part of this Disclaimer is held by a court of competent
          jurisdiction to be unenforceable, the remaining provisions shall
          remain in full force and effect.
        </p>
      </main>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mt-8">
      <h2 className="fc-heading text-lg text-[#1C1C1C]">{title}</h2>
      <div className="text-[15px] text-[#1C1C1C] leading-relaxed mt-3 space-y-3">
        {children}
      </div>
    </section>
  );
}
